import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { skillApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Spinner, EmptyState } from '../../components/shared/index';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  PlusIcon,
  TrashIcon,
  CheckBadgeIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

const LEVEL_STYLES = {
  novice:       'bg-gray-100   text-gray-700   border-gray-200',
  intermediate: 'bg-blue-50    text-blue-700   border-blue-100',
  advanced:     'bg-indigo-50  text-indigo-700 border-indigo-100',
  expert:       'bg-green-50   text-green-700  border-green-100',
};

const LEVELS = ['novice', 'intermediate', 'advanced', 'expert'];

export default function EmployeeSkillsTab({ employeeId }) {
  const qc = useQueryClient();
  const { user } = useAuth();

  const canEndorse = user?.role === 'hr_admin' || user?.role === 'manager' || user?.role === 'super_admin';

  const [skill, setSkill]     = useState('');
  const [level, setLevel]     = useState('intermediate');
  const [years, setYears]     = useState('0');

  const { data: skills = [], isLoading } = useQuery({
    queryKey: ['employee-skills', employeeId],
    queryFn:  () => skillApi.listForEmployee(employeeId).then(r => r.data),
    enabled:  !!employeeId,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['employee-skills', employeeId] });

  const addMut = useMutation({
    mutationFn: data => skillApi.add(employeeId, data),
    onSuccess:  () => {
      toast.success('Skill added');
      setSkill(''); setLevel('intermediate'); setYears('0');
      invalidate();
    },
    onError: e => toast.error(e.response?.data?.message || 'Failed to add skill'),
  });

  const removeMut = useMutation({
    mutationFn: id => skillApi.remove(employeeId, id),
    onSuccess:  () => { toast.success('Skill removed'); invalidate(); },
    onError:    e => toast.error(e.response?.data?.message || 'Failed to remove skill'),
  });

  const endorseMut = useMutation({
    mutationFn: id => skillApi.endorse(employeeId, id),
    onSuccess:  () => { toast.success('Skill endorsed'); invalidate(); },
    onError:    e => toast.error(e.response?.data?.message || 'Failed to endorse'),
  });

  const handleAdd = () => {
    const t = skill.trim();
    if (!t) {
      toast.error('Skill name is required');
      return;
    }
    addMut.mutate({
      skill: t,
      level,
      years_experience: Number(years) || 0,
    });
  };

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <SparklesIcon className="w-5 h-5 text-indigo-600" />
          <h2 className="section-title">Skills & competencies</h2>
        </div>
        <span className="text-xs text-gray-500">{skills.length} recorded</span>
      </div>

      {/* Add form */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 mb-4">
        <input
          className="input md:col-span-5"
          placeholder="e.g. React, SQL, Team leadership"
          value={skill}
          onChange={e => setSkill(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
        />
        <select className="input md:col-span-3" value={level} onChange={e => setLevel(e.target.value)}>
          {LEVELS.map(l => <option key={l} value={l} className="capitalize">{l}</option>)}
        </select>
        <input
          className="input md:col-span-2"
          type="number"
          min="0"
          step="0.5"
          placeholder="Years"
          value={years}
          onChange={e => setYears(e.target.value)}
        />
        <button
          className="btn-primary md:col-span-2 inline-flex items-center justify-center gap-1 disabled:opacity-40"
          disabled={addMut.isPending}
          onClick={handleAdd}
        >
          <PlusIcon className="w-4 h-4" />
          {addMut.isPending ? 'Adding…' : 'Add'}
        </button>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex justify-center p-10"><Spinner size="md" /></div>
      ) : !skills.length ? (
        <EmptyState
          title="No skills recorded"
          description="Add the first skill to build this employee's competency profile."
        />
      ) : (
        <div className="divide-y divide-gray-100">
          {skills.map(s => (
            <div key={s.id} className="flex items-center gap-3 py-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-medium text-gray-900 capitalize">{s.skill}</p>
                  <span className={clsx(
                    'inline-flex px-1.5 py-0.5 text-[10px] rounded border font-medium capitalize',
                    LEVEL_STYLES[s.level] || LEVEL_STYLES.intermediate
                  )}>
                    {s.level}
                  </span>
                  {s.endorsed_by && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-green-700">
                      <CheckBadgeIcon className="w-3.5 h-3.5" />
                      Endorsed{s.endorser?.name ? ` by ${s.endorser.name}` : ''}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  {Number(s.years_experience || 0)} yrs · {s.is_self_reported ? 'self-reported' : 'verified'}
                </p>
              </div>
              {canEndorse && !s.endorsed_by && (
                <button
                  onClick={() => endorseMut.mutate(s.id)}
                  disabled={endorseMut.isPending}
                  className="btn-sm btn-secondary inline-flex items-center gap-1"
                  title="Endorse this skill"
                >
                  <CheckBadgeIcon className="w-4 h-4" />
                  Endorse
                </button>
              )}
              <button
                onClick={() => removeMut.mutate(s.id)}
                disabled={removeMut.isPending}
                className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors"
                title="Remove"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
